﻿import { Tabs, Card, Form, Input, Button, App, Spin, Alert, Upload, InputNumber, List, Modal, Popconfirm, Row, Col, Switch } from 'antd';
import type { FormInstance, UploadProps } from 'antd';
import { InfoCircleOutlined, UploadOutlined, MobileOutlined, PictureOutlined, LockOutlined, TagsOutlined, ProfileOutlined, FieldTimeOutlined, ScheduleOutlined, DownloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { del, get, patch, post, put } from '../../api/client';
import { useAuthStore } from '../../stores/authStore';
import { OrderCatalogPanel } from './components/OrderCatalogPanel';
import { BackfillPolicyPanel } from './components/BackfillPolicyPanel';
import { OrderPolicyPanel } from './components/OrderPolicyPanel';
import type { NamespaceConfig } from './components/NamespaceConfigForm';

type ImageUploader = Parameters<NonNullable<UploadProps['customRequest']>>[0];

/**
 * 站点图片上传：把返回的相对路径写进指定表单字段。
 * Logo 与 Favicon 共用 /uploads/image，两处只是落库列不同。
 */
function makeImageUploader(
  form: FormInstance,
  message: ReturnType<typeof App.useApp>['message'],
  field: string,
  label: string,
) {
  return async ({ file, onSuccess, onError }: ImageUploader) => {
    try {
      const fd = new FormData();
      fd.append('file', file as File);
      const resp = await post<{ url: string }>('/uploads/image', fd);
      form.setFieldsValue({ [field]: resp.url });
      message.success(`${label} 已上传，请记得保存`);
      onSuccess?.(resp);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '上传失败');
      onError?.(error as Error);
    }
  };
}

interface SystemInfo {
  siteName: string;
  companyName?: string;
  contactPhone?: string;
  contactEmail?: string;
  // 对外服务时间（如「09:00-18:00」或「周一至周五 09:00-18:00」）：联系页展示用自由文本，留空则整行不渲染。
  contactHours?: string;
  address?: string;
  copyright?: string;
  icpNumber?: string;
  policeNumber?: string;
  appFilingNumber?: string;
  logoUrl?: string;
  faviconUrl?: string;
  // App 下载页读数（web /download）：三平台各自的上架开关 + 链接 + 版本号，Android 另带包体/更新信息。
  androidEnabled?: boolean;
  androidUrl?: string;
  androidVersion?: string;
  androidSize?: string;
  androidUpdatedAt?: string;
  androidChangelog?: string;
  iosEnabled?: boolean;
  iosUrl?: string;
  iosVersion?: string;
  harmonyEnabled?: boolean;
  harmonyUrl?: string;
  harmonyVersion?: string;
  // 用户端首页/登录页/关于页的四格统计读数（展示用自由文本，留空＝那一格整块不显示）。
  // 见 backend schema 注释：对外宣传的统计是广告引证内容，只能填真实可对账的数。
  statUsers?: string;
  statOrders?: string;
  statYears?: string;
  statResponse?: string;
}

function SiteInfoPanel() {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const [saving, setSaving] = useState(false);
  const uploadLogo = makeImageUploader(form, message, 'logoUrl', 'Logo');
  const uploadFavicon = makeImageUploader(form, message, 'faviconUrl', 'Favicon');
  const { data, isLoading } = useQuery({
    queryKey: ['system-info'],
    queryFn: () => get<SystemInfo>('/admin/system-info'),
  });

  if (isLoading) return <Spin />;

  return (
    <Card title="站点基本信息">
      <Form
        form={form}
        layout="vertical"
        initialValues={data}
        onFinish={async (values) => {
          setSaving(true);
          try {
            await patch('/admin/system-info', values);
            message.success('站点信息已保存');
          } catch (error) {
            message.error(error instanceof Error ? error.message : '保存失败');
          } finally {
            setSaving(false);
          }
        }}
        style={{ maxWidth: 560 }}
      >
        <Form.Item
          name="siteName"
          label="网站名称"
          tooltip="显示在浏览器标签页标题、导航栏与站点页脚；修改后各端下次加载生效。请与 ICP 备案的网站名称保持一致（核查会比对）"
          rules={[{ required: true, message: '请输入网站名称' }]}
        >
          <Input placeholder="活动精英订单预约管理系统" />
        </Form.Item>
        <Form.Item
          name="companyName"
          label="公司全称"
          tooltip="主体全称，须逐字等于营业执照与 ICP 备案主体名称——应用市场审核会拿它反查备案主体，对不上直接驳回。展示在 APP 与鸿蒙端「设置 → 通用 → 公司信息」第一行；Web 端主体名由版权行承载。留空则整行不显示"
          extra="示例：＿＿＿＿文化传播有限公司（不要写简称、品牌名或网站名称）"
        >
          <Input placeholder="未确定则留空，各端不显示该行" allowClear />
        </Form.Item>
        <Form.Item name="contactPhone" label="客服电话" tooltip="展示在客服中心页面与站点页脚，建议填写可接听的服务电话">
          <Input placeholder="400-xxx-xxxx" />
        </Form.Item>
        <Form.Item name="contactEmail" label="客服邮箱" tooltip="展示在客服中心页面与站点页脚">
          <Input placeholder="service@example.com" />
        </Form.Item>
        <Form.Item
          name="contactHours"
          label="工作时间"
          tooltip="展示在「联系我们」页的工作时间行，自由文本原样显示（如「09:00-18:00」或「周一至周五 09:00-18:00」）。留空则联系页整行不显示"
        >
          <Input placeholder="周一至周五 09:00-18:00（留空则联系页不显示该行）" allowClear />
        </Form.Item>
        <Form.Item name="address" label="公司地址">
          <Input />
        </Form.Item>
        <Form.Item
          name="copyright"
          label="版权信息"
          tooltip="页脚展示的版权行，主体名称须与 ICP 备案主体一致（留空则退回「© 当年 网站名称 版权所有」）"
        >
          <Input placeholder="© 2026 ＿＿有限公司" />
        </Form.Item>
        <Form.Item
          name="icpNumber"
          label="ICP 备案号"
          tooltip="必须是本主体自己批下来的号——挂他主体的备案号属于冒用，接入商核查与 APP 上架审核都会直接判不通过。留空则页脚不显示这条。填入后页脚会链到 beian.miit.gov.cn"
          extra="形如：豫ICP备2023003773号-3（网站名称需与备案网站名称一致）"
          rules={[
            {
              // 填了就必须像一个号：打错成查无此号的样子，页脚挂着照样过不了核查
              pattern: /^[\u4e00-\u9fa5]{1,3}ICP备\d{6,12}号(-\d{1,4})?$/,
              message: '备案号格式不对，正确形如「豫ICP备2023003773号-3」',
            },
          ]}
        >
          <Input placeholder="留空＝本主体还没有备案号，页脚不显示" allowClear />
        </Form.Item>
        <Form.Item
          name="policeNumber"
          label="公安备案号"
          tooltip="全国互联网安全管理服务平台（beian.mps.gov.cn）下发的号，未申请/未下发就留空，页脚不显示这一条"
          extra="形如：豫公网安备41010502000000号"
          rules={[
            {
              pattern: /^[\u4e00-\u9fa5]{1,3}公网安备\d{10,16}号$/,
              message: '公安备案号格式不对，正确形如「豫公网安备41010502000000号」',
            },
          ]}
        >
          <Input placeholder="未下发则留空" allowClear />
        </Form.Item>
        <Form.Item
          name="appFilingNumber"
          label="APP 备案号"
          tooltip="工信部「移动互联网应用程序备案」单独下发的号，与上面的网站 ICP 备案号是两个号（本后缀带字母 A），两者不可互相顶替——上架审核按号反查到的备案类型必须与应用一致。应用市场必填，填了会显示在 APP 的「设置 → 通用 → 公司信息」里；还没批下来就留空，该行不展示"
          extra="形如：豫ICP备2023003773号-5A（按包名 + 签名证书指纹 + 应用名称申请，三者与实际不一致会被驳回）"
          rules={[
            {
              pattern: /^[\u4e00-\u9fa5]{1,3}ICP备\d{6,12}号-\d{1,4}A$/,
              message: 'APP 备案号格式不对，正确形如「豫ICP备2023003773号-5A」（网站号没有末尾的 A）',
            },
          ]}
        >
          <Input placeholder="未备案则留空，APP 内不显示该行" allowClear />
        </Form.Item>
        <Form.Item
          name="logoUrl"
          label="Logo 图片（上传或填写地址）"
          extra="站点 Logo，同时用于 APP 启动欢迎页展示。地址建议填 /uploads/images/… 这种站内相对路径，换域名不用改"
        >
          <Input
            placeholder="点击右侧按钮上传，或直接粘贴图片地址"
            suffix={
              <Upload accept="image/*" showUploadList={false} customRequest={uploadLogo}>
                <PictureOutlined style={{ cursor: 'pointer' }} />
              </Upload>
            }
          />
        </Form.Item>
        <Form.Item
          name="faviconUrl"
          label="Favicon 图片（上传或填写地址）"
          tooltip="浏览器标签页与收藏夹里的小图标。这一个值同时驱动用户端与管理端两处标签页：用户端在下次加载时生效，管理端在管理后台刷新时生效。浏览器对图标缓存很顽固，改完看不到变化先强制刷新（Ctrl+Shift+R）或新开一个标签页"
          extra="建议 64×64 或 128×128 的方形图。上传口只收 JPG/PNG/WEBP/GIF/BMP，现有 .ico 想直接用的话把地址填进这一栏（如 /favicon.ico）；留空＝用户端与管理端都退回构建自带的 /favicon.ico"
        >
          <Input
            placeholder="点击右侧按钮上传，或直接粘贴图片地址"
            suffix={
              <Upload accept="image/*" showUploadList={false} customRequest={uploadFavicon}>
                <PictureOutlined style={{ cursor: 'pointer' }} />
              </Upload>
            }
          />
        </Form.Item>

        <div style={{ fontSize: 14, fontWeight: 600, color: 'rgba(0,0,0,0.88)', margin: '8px 0 12px' }}>
          宣传统计读数
        </div>
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          title="只填能对得上真实统计的数字，宁可留空"
          description="用户端首页、登录页、关于页的那四格读数全部来自下面四栏，填什么就显示什么。这类对外宣传的统计属于广告引证内容，《广告法》第十一条第二款要求引证内容真实、准确并标明出处，写一个对不上的数量级就是虚假宣传（第五十九条：责令停止发布并处广告主十万元以下罚款）。留空的那一格在三页上整块不显示——不显示比编一个数安全。"
        />
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="statUsers" label="活跃用户" extra="按后台/数据库真实统计填写，如「8,600+」">
              <Input placeholder="留空则不显示该格" allowClear maxLength={50} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="statOrders" label="累计订单" extra="如「23 万笔」，口径自己定、但要能对上账">
              <Input placeholder="留空则不显示该格" allowClear maxLength={50} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="statYears" label="稳定运行" extra="如「自 2021 年」">
              <Input placeholder="留空则不显示该格" allowClear maxLength={50} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="statResponse" label="服务响应" extra="如「工作日 2 小时内」，别写做不到的承诺">
              <Input placeholder="留空则不显示该格" allowClear maxLength={50} />
            </Form.Item>
          </Col>
        </Row>

        <Button type="primary" htmlType="submit" loading={saving}>
          保存站点信息
        </Button>
      </Form>
    </Card>
  );
}

function AppDownloadPanel() {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const [saving, setSaving] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['system-info'],
    queryFn: () => get<SystemInfo>('/admin/system-info'),
  });

  if (isLoading) return <Spin />;

  return (
    <Card title="App 下载页" extra={<DownloadOutlined style={{ fontSize: 18 }} />}>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16, maxWidth: 560 }}
        title="下载页读数全部来自这里，改完即生效、无需重新构建前端"
        description="用户端 /download 页实时读取以下配置。哪个平台还没上架就把它的开关关掉——下载页会整块置灰显示「敬请期待」。已上架的平台会按其链接自动生成二维码：Android 填自托管 APK 直链（先把安装包传到服务器，如站点目录 /app/ 下），手机扫码即下；iOS / 鸿蒙填各自商店链接（App Store / 华为应用市场），扫码直达商店页——这两端系统不允许自托管安装包，只能走商店。"
      />
      <Form
        form={form}
        layout="vertical"
        initialValues={data}
        onFinish={async (values) => {
          setSaving(true);
          try {
            await patch('/admin/system-info', values);
            message.success('下载页配置已保存');
          } catch (error) {
            message.error(error instanceof Error ? error.message : '保存失败');
          } finally {
            setSaving(false);
          }
        }}
        style={{ maxWidth: 560 }}
      >
        <Form.Item
          name="androidEnabled"
          label="Android 已上架（可下载）"
          valuePropName="checked"
          tooltip="打开后下载页展示 Android 的二维码与下载按钮；关闭则整块置灰显示「敬请期待」"
        >
          <Switch />
        </Form.Item>
        <Form.Item
          name="androidUrl"
          label="APK 下载地址（自托管直链）"
          extra="示例：https://huodongjingying.com/app/booking-release.apk　留空则不显示下载按钮"
          rules={[{ type: 'url', warningOnly: true, message: '这不是一个合法的 URL，请检查' }]}
        >
          <Input placeholder="https://.../booking-release.apk" allowClear />
        </Form.Item>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name="androidVersion" label="版本号" extra="如 1.0.3">
              <Input placeholder="1.0.0" allowClear />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="androidSize" label="包体大小" extra="展示用文本，如「约 38 MB」">
              <Input placeholder="约 38 MB" allowClear />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="androidUpdatedAt" label="更新时间" extra="展示用文本，如「2026-10-12」">
              <Input placeholder="2026-10-12" allowClear />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="androidChangelog" label="更新说明" extra="多行，展示最近版本的变更；留空则下载页不显示这一块">
          <Input.TextArea rows={3} placeholder="本次更新内容…" allowClear />
        </Form.Item>

        <Form.Item
          name="iosEnabled"
          label="iOS 已上架（App Store）"
          valuePropName="checked"
          tooltip="上架后打开并填写 App Store 链接；当前未上架保持关闭，下载页显示「敬请期待」"
        >
          <Switch />
        </Form.Item>
        <Row gutter={12}>
          <Col span={16}>
            <Form.Item
              name="iosUrl"
              label="App Store 链接"
              rules={[{ type: 'url', warningOnly: true, message: '这不是一个合法的 URL，请检查' }]}
            >
              <Input placeholder="https://apps.apple.com/cn/app/..." allowClear />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="iosVersion" label="版本号">
              <Input placeholder="1.0.0" allowClear />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="harmonyEnabled"
          label="鸿蒙已上架（华为应用市场）"
          valuePropName="checked"
          tooltip="上架后打开并填写 AppGallery 链接；当前未上架保持关闭，下载页显示「敬请期待」"
        >
          <Switch />
        </Form.Item>
        <Row gutter={12}>
          <Col span={16}>
            <Form.Item
              name="harmonyUrl"
              label="华为应用市场链接"
              rules={[{ type: 'url', warningOnly: true, message: '这不是一个合法的 URL，请检查' }]}
            >
              <Input placeholder="https://appgallery.huawei.com/app/..." allowClear />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="harmonyVersion" label="版本号">
              <Input placeholder="1.0.0" allowClear />
            </Form.Item>
          </Col>
        </Row>

        <Button type="primary" htmlType="submit" loading={saving}>
          保存下载页配置
        </Button>
      </Form>
    </Card>
  );
}

function SecurityPanel() {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const [saving, setSaving] = useState(false);
  const signOut = useAuthStore((s) => s.signOut);

  return (
    <Card title="账号安全">
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16, maxWidth: 560 }}
        title="修改密码后需重新登录"
        description="为保障账号安全，修改成功后请使用新密码重新登录管理后台。"
      />
      <Form
        form={form}
        layout="vertical"
        style={{ maxWidth: 420 }}
        onFinish={async (values) => {
          setSaving(true);
          try {
            await put('/users/me/password', {
              currentPassword: values.currentPassword,
              newPassword: values.newPassword,
            });
            message.success('密码修改成功，请使用新密码重新登录');
            // 后端改密即吊销全部 refresh_session，当前 accessToken 也已失效，
            // 主动登出清空状态；App 的 !user 守卫会把页面送回登录页
            await signOut();
          } catch (error) {
            message.error(error instanceof Error ? error.message : '密码修改失败');
            setSaving(false);
          }
        }}
      >
        <Form.Item
          name="currentPassword"
          label="当前密码"
          rules={[{ required: true, message: '请输入当前密码' }]}
        >
          <Input.Password placeholder="请输入当前登录密码" />
        </Form.Item>
        <Form.Item
          name="newPassword"
          label="新密码"
          rules={[
            { required: true, message: '请输入新密码' },
            { min: 8, message: '新密码至少 8 位' },
          ]}
        >
          <Input.Password placeholder="至少 8 位，建议包含字母、数字与符号" />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          label="确认新密码"
          dependencies={['newPassword']}
          rules={[
            { required: true, message: '请再次输入新密码' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('两次输入的密码不一致'));
              },
            }),
          ]}
        >
          <Input.Password placeholder="请再次输入新密码" />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={saving}>
          修改密码
        </Button>
      </Form>
    </Card>
  );
}


function AppPanel() {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const [saving, setSaving] = useState(false);
  const [splashImage, setSplashImage] = useState('');
  const [launchScreenImage, setLaunchScreenImage] = useState('');
  const { data, isLoading } = useQuery({
    queryKey: ['system-config', 'app'],
    queryFn: async () => (await get<unknown>('/admin/system-config/app')) as NamespaceConfig,
  });

  useEffect(() => {
    if (data?.values) {
      const values = data.values as Record<string, unknown>;
      setSplashImage(String(values.splashImage || ''));
      setLaunchScreenImage(String(values.launchScreenImage || ''));
      form.setFieldsValue({ splashDuration: Number(values.splashDuration ?? 2) });
    }
  }, [data, form]);

  if (isLoading || !data) return <Spin />;

  return (
    <Card
      title="APP 启动配置"
      styles={{ body: { paddingTop: 8 } }}
      extra={<MobileOutlined style={{ fontSize: 18 }} />}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16, maxWidth: 560 }}
        title="启动页展示规则"
        description="欢迎页图片：APP 启动后第一屏全屏展示（建议 1080×1920 以上竖图），未配置时展示站点 Logo 与网站名称。原生冷启动屏图片：点击桌面图标后系统启动屏使用，配置后需重新打包安装 APK 方可生效。"
      />
      <Form
        form={form}
        layout="vertical"
        style={{ maxWidth: 560 }}
        onFinish={async (values) => {
          setSaving(true);
          try {
            await patch('/admin/system-config/app', {
              splashImage,
              launchScreenImage,
              splashDuration: values.splashDuration ?? 2,
            });
            message.success('APP 启动配置已保存');
          } catch (error) {
            message.error(error instanceof Error ? error.message : '保存失败');
          } finally {
            setSaving(false);
          }
        }}
      >
        <Form.Item label="欢迎页图片（APP 启动后第一屏）" help="点击上传后需保存生效；更换图片请重新上传并保存">
          <Upload
            accept="image/*"
            showUploadList={false}
            customRequest={async ({ file, onSuccess, onError }) => {
              try {
                const fd = new FormData();
                fd.append('file', file as File);
                const resp = await post<{ url: string }>('/uploads/image', fd);
                setSplashImage(resp.url);
                message.success('图片已上传，请点击保存');
                onSuccess?.(resp);
              } catch (error) {
                message.error(error instanceof Error ? error.message : '上传失败');
                onError?.(error as Error);
              }
            }}
          >
            <Button icon={<UploadOutlined />}>上传欢迎页图片</Button>
          </Upload>
        </Form.Item>
        {splashImage && (
          <Form.Item label="当前欢迎页图片预览">
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <img
                src={splashImage}
                alt="欢迎页预览"
                style={{ maxWidth: 180, maxHeight: 320, borderRadius: 8, border: '1px solid #eee' }}
              />
              <Button danger size="small" onClick={() => setSplashImage('')}>
                移除图片
              </Button>
            </div>
          </Form.Item>
        )}
        <Form.Item
          label="原生冷启动屏图片（点击图标后的系统启动屏）"
          help="与欢迎页图片相互独立；保存后需重新打包安装 APK 方可生效"
        >
          <Upload
            accept="image/*"
            showUploadList={false}
            customRequest={async ({ file, onSuccess, onError }) => {
              try {
                const fd = new FormData();
                fd.append('file', file as File);
                const resp = await post<{ url: string }>('/uploads/image', fd);
                setLaunchScreenImage(resp.url);
                message.success('图片已上传，请点击保存');
                onSuccess?.(resp);
              } catch (error) {
                message.error(error instanceof Error ? error.message : '上传失败');
                onError?.(error as Error);
              }
            }}
          >
            <Button icon={<UploadOutlined />}>上传冷启动屏图片</Button>
          </Upload>
        </Form.Item>
        {launchScreenImage && (
          <Form.Item label="当前冷启动屏图片预览">
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <img
                src={launchScreenImage}
                alt="冷启动屏预览"
                style={{ maxWidth: 180, maxHeight: 320, borderRadius: 8, border: '1px solid #eee' }}
              />
              <Button danger size="small" onClick={() => setLaunchScreenImage('')}>
                移除图片
              </Button>
            </div>
          </Form.Item>
        )}
        <Form.Item name="splashDuration" label="启动页展示时长（秒）" initialValue={2}>
          <InputNumber min={1} max={10} style={{ width: 160 }} />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={saving}>
          保存 APP 配置
        </Button>
      </Form>
    </Card>
  );
}


interface CatalogTypeItem {
  id: string;
  name: string;
  createdBy: string | null;
  createdAt: string;
}

interface CatalogTagItem {
  id: string;
  name: string;
  color: string | null;
  createdBy: string | null;
  createdAt: string;
}

interface EditTarget {
  kind: 'type' | 'tag';
  id: string;
  name: string;
  color?: string | null;
}

function AdminCatalogPanel() {
  const { message } = App.useApp();
  const [addKind, setAddKind] = useState<'type' | 'tag' | null>(null);
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);
  const [catalogForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const typesQuery = useQuery({
    queryKey: ['admin-catalog-types'],
    queryFn: () => get<CatalogTypeItem[]>('/customer-types'),
  });
  const tagsQuery = useQuery({
    queryKey: ['admin-catalog-tags'],
    queryFn: () => get<CatalogTagItem[]>('/customer-tags'),
  });

  const presetTypes = (typesQuery.data ?? []).filter((item) => !item.createdBy);
  const presetTags = (tagsQuery.data ?? []).filter((item) => !item.createdBy);

  const refreshCatalog = () => {
    void typesQuery.refetch();
    void tagsQuery.refetch();
  };

  const onAddSubmit = async () => {
    const values = await catalogForm.validateFields();
    try {
      if (addKind === 'type') {
        await post('/admin/catalog/preset-types', { name: values.name });
        message.success('系统预设客户类型已创建');
      } else {
        await post('/admin/catalog/preset-tags', { name: values.name, color: values.color || undefined });
        message.success('系统预设客户标签已创建');
      }
      setAddKind(null);
      catalogForm.resetFields();
      refreshCatalog();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '创建失败');
    }
  };

  const onEditSubmit = async () => {
    if (!editTarget) return;
    const values = await editForm.validateFields();
    try {
      if (editTarget.kind === 'type') {
        await put(`/customer-types/${editTarget.id}`, { name: values.name });
      } else {
        await put(`/customer-tags/${editTarget.id}`, {
          name: values.name,
          ...(values.color !== undefined ? { color: values.color } : {}),
        });
      }
      message.success('预设项已更新');
      setEditTarget(null);
      refreshCatalog();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '修改失败');
    }
  };

  const onDelete = async (kind: 'type' | 'tag', id: string) => {
    try {
      await del(kind === 'type' ? `/customer-types/${id}` : `/customer-tags/${id}`);
      message.success('预设项已删除');
      refreshCatalog();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '删除失败');
    }
  };

  const renderActions = (kind: 'type' | 'tag', item: EditTarget) => [
    <Button
      key="edit"
      size="small"
      type="link"
      onClick={() => {
        setEditTarget(item);
        editForm.setFieldsValue({ name: item.name, color: item.color ?? undefined });
      }}
    >
      编辑
    </Button>,
    <Popconfirm
      key="delete"
      title="确定删除该预设项？"
      description="删除后各端将不再展示该预设项"
      onConfirm={() => void onDelete(kind, item.id)}
    >
      <Button size="small" type="link" danger>
        删除
      </Button>
    </Popconfirm>,
  ];

  return (
    <Card title="客户目录（客户类型 / 客户标签）" styles={{ body: { paddingTop: 8 } }}>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16, maxWidth: 720 }}
        title="系统预设与用户私有标签"
        description="系统预设项（下方列表）对所有用户共享；用户在业务端自行创建的标签仅本人可见。预设标签删除受自动规则约束：用户视角下至少保留一个标签；有自定义标签后预设标签才可删除。"
      />
      <Row gutter={24}>
        <Col xs={24} lg={12}>
          <Card
            type="inner"
            title="系统预设客户类型"
            extra={
              <Button size="small" type="primary" ghost onClick={() => { setAddKind('type'); catalogForm.resetFields(); }}>
                新增预设
              </Button>
            }
          >
            <List
              size="small"
              loading={typesQuery.isLoading}
              dataSource={presetTypes}
              locale={{ emptyText: '暂无系统预设客户类型' }}
              renderItem={(item) => (
                <List.Item actions={renderActions('type', { kind: 'type', id: item.id, name: item.name })}>
                  <List.Item.Meta title={item.name} />
                </List.Item>
              )}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            type="inner"
            title="系统预设客户标签"
            extra={
              <Button size="small" type="primary" ghost onClick={() => { setAddKind('tag'); catalogForm.resetFields(); }}>
                新增预设
              </Button>
            }
          >
            <List
              size="small"
              loading={tagsQuery.isLoading}
              dataSource={presetTags}
              locale={{ emptyText: '暂无系统预设客户标签' }}
              renderItem={(item) => (
                <List.Item
                  actions={renderActions('tag', { kind: 'tag', id: item.id, name: item.name, color: item.color })}
                >
                  <List.Item.Meta
                    title={
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            background: item.color || '#bfbfbf',
                            display: 'inline-block',
                          }}
                        />
                        {item.name}
                      </span>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>

      <Modal
        title={addKind === 'type' ? '新增系统预设客户类型' : '新增系统预设客户标签'}
        open={addKind !== null}
        okText="创建"
        cancelText="取消"
        onOk={() => void onAddSubmit()}
        onCancel={() => setAddKind(null)}
        destroyOnHidden
      >
        <Form form={catalogForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label={addKind === 'type' ? '类型名称' : '标签名称'}
            rules={[{ required: true, message: '请输入名称' }]}
          >
            <Input placeholder={addKind === 'type' ? '如：企业客户' : '如：重点客户'} maxLength={20} />
          </Form.Item>
          {addKind === 'tag' && (
            <Form.Item name="color" label="标签颜色（可选）">
              <Input placeholder="如 #52c41a，留空则显示默认灰色" maxLength={20} />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        title={editTarget?.kind === 'type' ? '编辑系统预设客户类型' : '编辑系统预设客户标签'}
        open={editTarget !== null}
        okText="保存"
        cancelText="取消"
        onOk={() => void onEditSubmit()}
        onCancel={() => setEditTarget(null)}
        destroyOnHidden
      >
        <Form form={editForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label={editTarget?.kind === 'type' ? '类型名称' : '标签名称'}
            rules={[{ required: true, message: '请输入名称' }]}
          >
            <Input maxLength={20} />
          </Form.Item>
          {editTarget?.kind === 'tag' && (
            <Form.Item name="color" label="标签颜色">
              <Input placeholder="如 #52c41a" maxLength={20} />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </Card>
  );
}


export default function SettingsPage() {
  return (
    <Card styles={{ body: { paddingTop: 8 } }}>
      <Tabs
        items={[
          {
            key: 'site',
            label: (
              <span>
                <InfoCircleOutlined /> 基本信息
              </span>
            ),
            children: <SiteInfoPanel />,
          },
          {
            key: 'app',
            label: (
              <span>
                <MobileOutlined /> APP 启动配置
              </span>
            ),
            children: <AppPanel />,
          },
          {
            key: 'app-download',
            label: (
              <span>
                <DownloadOutlined /> App 下载页
              </span>
            ),
            children: <AppDownloadPanel />,
          },
          {
            key: 'catalog',
            label: (
              <span>
                <TagsOutlined /> 客户目录
              </span>
            ),
            children: <AdminCatalogPanel />,
          },
          {
            key: 'order-catalog',
            label: (
              <span>
                <ProfileOutlined /> 订单目录
              </span>
            ),
            children: <OrderCatalogPanel />,
          },
          {
            key: 'backfill',
            label: (
              <span>
                <FieldTimeOutlined /> 补录策略
              </span>
            ),
            children: <BackfillPolicyPanel />,
          },
          {
            key: 'order-policy',
            label: (
              <span>
                <ScheduleOutlined /> 订单策略
              </span>
            ),
            children: <OrderPolicyPanel />,
          },
          {
            key: 'security',
            label: (
              <span>
                <LockOutlined /> 账号安全
              </span>
            ),
            children: <SecurityPanel />,
          },
        ]}
      />
    </Card>
  );
}